// =============================================================================
// publication-client.http.ts — Adaptateur HTTP RÉEL du PublicationClient (S18 / stream C).
// =============================================================================
// Câble la SPA admin contre les surfaces GELÉES du stream C (PublicationController). Substitué derrière le
// MÊME DI seam (PublicationClientProvider) : écrans/hooks consomment l'interface PublicationClient, l'impl est
// substituée sans toucher l'UI (stub-first ; cet adaptateur câble le réseau).
//
// AUTORITÉ = API/worker (NestJS, AdminSessionGuard, anonymisation des bands, transitions d'état,
// irréversibilité, exigence du double-confirm). Cet adaptateur ne porte AUCUNE règle métier : il sérialise
// l'intention, parse la réponse, mappe l'erreur (message serveur tel quel — y compris le 409 double-confirm).
//
// SÉCURITÉ (RULE_REF_006, parité rank-admin-client.http.ts / campaign-client.http.ts) : le secret de session
// admin (cookie httpOnly) n'est JAMAIS lu ni fabriqué ici ; il est transmis par credentials: 'include'. Le GET
// (preview) n'exige AUCUN en-tête CSRF ; les MUTATIONS (validate / publish) ré-émettent le cookie NON-httpOnly
// `admin_csrf` en en-tête `x-csrf-token` (double-submit). Sans cet en-tête, l'écriture admin est 403 côté
// serveur (fail-closed). Les drapeaux de confirmation viennent du body ; l'auteur (validateur/publieur) est
// server-set (session). Le campaignId est la ressource d'URL.

import {
  PublicationApiError,
  type PreviewSnapshotDto,
  type PublicationClient,
  type PublishInput,
  type PublishResultDto,
  type UnpublishInput,
  type UnpublishResultDto,
  type ValidateAnalysisResultDto,
} from '@/api/publication-client';

/** Forme attendue d'un corps d'erreur structuré (NestJS HttpException). */
interface StructuredErrorBody {
  readonly statusCode?: number;
  readonly message?: string;
  /** Code d'erreur applicatif stable (ex. ANALYSIS_UNPUBLISH_MOTIF_REQUIRED), quand l'API en émet un. */
  readonly code?: string;
}

/** Nom du cookie CSRF NON-httpOnly posé au login admin (double-submit, cf. API CSRF_COOKIE). */
const CSRF_COOKIE_NAME = 'admin_csrf';

/**
 * Lit la valeur du cookie CSRF `admin_csrf` (NON-httpOnly, lisible par le SPA même origine). `undefined` si
 * absent (pas de session, ou contexte sans document) — l'appel mutant partira alors sans en-tête et l'API
 * répondra 403 (fail-closed côté serveur, jamais d'écriture).
 */
function readCsrfToken(): string | undefined {
  if (typeof document === 'undefined') {
    return undefined;
  }
  for (const part of document.cookie.split(';')) {
    const [rawName, ...rest] = part.trim().split('=');
    if (rawName === CSRF_COOKIE_NAME) {
      return decodeURIComponent(rest.join('='));
    }
  }
  return undefined;
}

/**
 * Adaptateur HTTP réel : `fetch` contre l'API admin publication. `baseUrl` par défaut vide (chemins relatifs :
 * SPA et API partagent l'origine, ou un reverse proxy route `/admin/*`). En test, on injecte un baseUrl ou on
 * mocke `fetch`.
 */
export class HttpPublicationClient implements PublicationClient {
  public constructor(private readonly baseUrl: string = '/api') {}

  /**
   * Exécute une requête et renvoie le JSON typé, ou lève une PublicationApiError (message serveur tel quel).
   * CSRF DOUBLE-SUBMIT sur les méthodes MUTANTES : l'en-tête `x-csrf-token` = valeur du cookie NON-httpOnly
   * `admin_csrf` (parité rank-admin-client.http.ts). Le secret de session (httpOnly) n'est JAMAIS lu ici.
   */
  private async request<T>(path: string, init: RequestInit): Promise<T> {
    const method = (init.method ?? 'GET').toUpperCase();
    const headers: Record<string, string> = {
      'content-type': 'application/json',
    };
    if (method !== 'GET' && method !== 'HEAD') {
      const csrf = readCsrfToken();
      if (csrf !== undefined) {
        headers['x-csrf-token'] = csrf;
      }
    }
    const response = await fetch(`${this.baseUrl}${path}`, {
      // Session admin = cookie httpOnly : transmis avec credentials: 'include'.
      credentials: 'include',
      ...init,
      headers: { ...headers, ...init.headers },
    });
    if (!response.ok) {
      throw await this.toApiError(response);
    }
    return (await response.json()) as T;
  }

  /** Convertit une réponse non-OK en erreur applicative typée (message serveur tel quel). */
  private async toApiError(response: Response): Promise<PublicationApiError> {
    let body: StructuredErrorBody | undefined;
    try {
      body = (await response.json()) as StructuredErrorBody;
    } catch {
      body = undefined;
    }
    const message =
      body?.message ??
      `Erreur API (${String(response.status)}) sur la publication du benchmark.`;
    return new PublicationApiError(message, response.status, body?.code);
  }

  public getPreview(campaignId: string): Promise<PreviewSnapshotDto> {
    return this.request<PreviewSnapshotDto>(
      `/admin/campaigns/${encodeURIComponent(campaignId)}/analysis/preview`,
      { method: 'GET' },
    );
  }

  public validateAnalysis(
    campaignId: string,
  ): Promise<ValidateAnalysisResultDto> {
    // Corps VIDE (la validation cible la campagne par son id d'URL + l'admin authentifié = server-set).
    return this.request<ValidateAnalysisResultDto>(
      `/admin/campaigns/${encodeURIComponent(campaignId)}/analysis/validate`,
      { method: 'POST', body: JSON.stringify({}) },
    );
  }

  public publish(
    campaignId: string,
    input: PublishInput,
  ): Promise<PublishResultDto> {
    return this.request<PublishResultDto>(
      `/admin/campaigns/${encodeURIComponent(campaignId)}/analysis/publish`,
      { method: 'POST', body: JSON.stringify(input) },
    );
  }

  public unpublish(
    campaignId: string,
    input: UnpublishInput,
  ): Promise<UnpublishResultDto> {
    // Le motif (obligatoire, non vide) part dans le body ; l'auteur (dé-publieur) est server-set (session).
    return this.request<UnpublishResultDto>(
      `/admin/campaigns/${encodeURIComponent(campaignId)}/analysis/unpublish`,
      { method: 'POST', body: JSON.stringify(input) },
    );
  }
}
